// Primary 2 English Learning Data - Part 1 (Topics 1-10)
// Based on Primary English Learning P2 Topics.txt

export const p2DataPart1 = {
  gradeId: 'p2',
  gradeName: 'Primary 2 (小學二年級)',
  objectives: [
    'Build on Primary 1 foundations with more complex grammar structures',
    'Develop reading comprehension and writing skills',
    'Learn advanced sentence patterns and verb forms',
    'Master time expressions and ordinal numbers',
    'Understand modal verbs and infinitives'
  ],
  keySkills: [
    'Prepositions of location and movement',
    'Modal verbs and infinitives',
    'Simple present tense with frequency adverbs',
    'Question formation and dialogue writing',
    'Time expressions and weather descriptions'
  ],
  topics: [
    {
      id: 'p2-topic-1',
      title: 'Prepositions of Location',
      description: 'Learning to describe where things are using prepositions like "in", "on", "under", "behind", "in front of"',
      purpose: 'To teach students how to use prepositions to describe the position or location of people, places, and things with more precision than Primary 1.',
      questions: [
        {
          question: 'The cat is _____ the table.',
          answer: 'under',
          explanation: 'Use "under" when something is below another object.'
        },
        {
          question: 'The book is _____ the shelf.',
          answer: 'on',
          explanation: 'Use "on" when something is touching the surface of another object.'
        },
        {
          question: 'The bird is _____ the cage.',
          answer: 'in',
          explanation: 'Use "in" when something is inside a container or enclosed space.'
        },
        {
          question: 'The car is _____ the house.',
          answer: 'in front of',
          explanation: 'Use "in front of" when something is ahead of another object.'
        },
        {
          question: 'The dog is _____ the tree.',
          answer: 'behind',
          explanation: 'Use "behind" when something is at the back of another object.'
        },
        {
          question: 'The picture is _____ the wall.',
          answer: 'on',
          explanation: 'Use "on" when something is attached to a surface.'
        },
        {
          question: 'The fish is _____ the water.',
          answer: 'in',
          explanation: 'Use "in" when something is inside a liquid or substance.'
        },
        {
          question: 'The ball is _____ the chair.',
          answer: 'under',
          explanation: 'Use "under" when something is below another object.'
        },
        {
          question: 'The boy is _____ the door.',
          answer: 'behind',
          explanation: 'Use "behind" when something is at the back of another object.'
        },
        {
          question: 'The cup is _____ the table.',
          answer: 'on',
          explanation: 'Use "on" when something is touching the surface of another object.'
        }
      ]
    },
    {
      id: 'p2-topic-2',
      title: 'Demonstrative Adjectives & Pronouns',
      description: 'Learning to use this, that, these, those as both adjectives and pronouns',
      purpose: 'To teach students how to use demonstrative words to point to specific people, places, or things, both as adjectives and pronouns.',
      questions: [
        {
          question: '_____ book is mine.',
          answer: 'This',
          explanation: 'Use "This" as a demonstrative adjective before a singular noun that is near.'
        },
        {
          question: '_____ are my friends.',
          answer: 'These',
          explanation: 'Use "These" as a demonstrative pronoun for plural things that are near.'
        },
        {
          question: '_____ car is expensive.',
          answer: 'That',
          explanation: 'Use "That" as a demonstrative adjective before a singular noun that is far.'
        },
        {
          question: '_____ are flowers.',
          answer: 'Those',
          explanation: 'Use "Those" as a demonstrative pronoun for plural things that are far.'
        },
        {
          question: '_____ house is big.',
          answer: 'This',
          explanation: 'Use "This" as a demonstrative adjective before a singular noun that is near.'
        },
        {
          question: '_____ are my toys.',
          answer: 'These',
          explanation: 'Use "These" as a demonstrative pronoun for plural things that are near.'
        },
        {
          question: '_____ tree is tall.',
          answer: 'That',
          explanation: 'Use "That" as a demonstrative adjective before a singular noun that is far.'
        },
        {
          question: '_____ are birds.',
          answer: 'Those',
          explanation: 'Use "Those" as a demonstrative pronoun for plural things that are far.'
        },
        {
          question: '_____ school is near.',
          answer: 'This',
          explanation: 'Use "This" as a demonstrative adjective before a singular noun that is near.'
        },
        {
          question: '_____ are my books.',
          answer: 'These',
          explanation: 'Use "These" as a demonstrative pronoun for plural things that are near.'
        }
      ]
    },
    {
      id: 'p2-topic-3',
      title: 'Imperatives',
      description: 'Learning to give commands and instructions using imperative sentences with more complexity',
      purpose: 'To teach students how to form and use imperative sentences to give commands, instructions, or make requests with more variety than Primary 1.',
      questions: [
        {
          question: 'Complete the command: "_____ the door, please."',
          answer: 'Open',
          explanation: 'Imperatives start with the base form of the verb (no subject needed).'
        },
        {
          question: 'Fill in the blank: "_____ your homework now."',
          answer: 'Do',
          explanation: 'Use the base form of the verb for imperatives.'
        },
        {
          question: 'Complete: "_____ quiet in the library."',
          answer: 'Be',
          explanation: 'Use "be" as the base form for the verb "to be" in imperatives.'
        },
        {
          question: 'Fill in the blank: "_____ your hands before eating."',
          answer: 'Wash',
          explanation: 'Use the base form of the verb for imperatives.'
        },
        {
          question: 'Complete: "_____ the window, it\'s cold."',
          answer: 'Close',
          explanation: 'Imperatives start with the base form of the verb.'
        },
        {
          question: 'Fill in the blank: "_____ your teeth twice a day."',
          answer: 'Brush',
          explanation: 'Use the base form of the verb for imperatives.'
        },
        {
          question: 'Complete: "_____ to the teacher."',
          answer: 'Listen',
          explanation: 'Imperatives start with the base form of the verb.'
        },
        {
          question: 'Fill in the blank: "_____ your room every day."',
          answer: 'Clean',
          explanation: 'Use the base form of the verb for imperatives.'
        },
        {
          question: 'Complete: "_____ the ball to me."',
          answer: 'Throw',
          explanation: 'Imperatives start with the base form of the verb.'
        },
        {
          question: 'Fill in the blank: "_____ your shoes before entering."',
          answer: 'Take off',
          explanation: 'Use the base form of the verb for imperatives.'
        }
      ]
    },
    {
      id: 'p2-topic-4',
      title: 'Ordinal Numbers',
      description: 'Learning to use ordinal numbers (first, second, third, etc.) to show order or position',
      purpose: 'To teach students how to use ordinal numbers to indicate order, position, or sequence of events.',
      questions: [
        {
          question: 'What is the ordinal number for 1?',
          answer: 'first',
          explanation: 'Use "first" for the number 1 in order.'
        },
        {
          question: 'What is the ordinal number for 2?',
          answer: 'second',
          explanation: 'Use "second" for the number 2 in order.'
        },
        {
          question: 'What is the ordinal number for 3?',
          answer: 'third',
          explanation: 'Use "third" for the number 3 in order.'
        },
        {
          question: 'What is the ordinal number for 4?',
          answer: 'fourth',
          explanation: 'Use "fourth" for the number 4 in order.'
        },
        {
          question: 'What is the ordinal number for 5?',
          answer: 'fifth',
          explanation: 'Use "fifth" for the number 5 in order.'
        },
        {
          question: 'What is the ordinal number for 6?',
          answer: 'sixth',
          explanation: 'Use "sixth" for the number 6 in order.'
        },
        {
          question: 'What is the ordinal number for 7?',
          answer: 'seventh',
          explanation: 'Use "seventh" for the number 7 in order.'
        },
        {
          question: 'What is the ordinal number for 8?',
          answer: 'eighth',
          explanation: 'Use "eighth" for the number 8 in order.'
        },
        {
          question: 'What is the ordinal number for 9?',
          answer: 'ninth',
          explanation: 'Use "ninth" for the number 9 in order.'
        },
        {
          question: 'What is the ordinal number for 10?',
          answer: 'tenth',
          explanation: 'Use "tenth" for the number 10 in order.'
        }
      ]
    },
    {
      id: 'p2-topic-5',
      title: 'Prepositions of Movement',
      description: 'Learning to describe movement using prepositions like "to", "from", "into", "out of", "up", "down"',
      purpose: 'To teach students how to use prepositions to describe the direction and movement of people, places, and things.',
      questions: [
        {
          question: 'I go _____ school every day.',
          answer: 'to',
          explanation: 'Use "to" to show movement towards a destination.'
        },
        {
          question: 'She comes _____ home.',
          answer: 'from',
          explanation: 'Use "from" to show the starting point of movement.'
        },
        {
          question: 'The cat jumps _____ the box.',
          answer: 'into',
          explanation: 'Use "into" to show movement inside a container.'
        },
        {
          question: 'The bird flies _____ the cage.',
          answer: 'out of',
          explanation: 'Use "out of" to show movement from inside to outside.'
        },
        {
          question: 'I walk _____ the stairs.',
          answer: 'up',
          explanation: 'Use "up" to show upward movement.'
        },
        {
          question: 'He runs _____ the hill.',
          answer: 'down',
          explanation: 'Use "down" to show downward movement.'
        },
        {
          question: 'We drive _____ the city.',
          answer: 'to',
          explanation: 'Use "to" to show movement towards a destination.'
        },
        {
          question: 'She returns _____ work.',
          answer: 'from',
          explanation: 'Use "from" to show the starting point of movement.'
        },
        {
          question: 'The dog goes _____ the house.',
          answer: 'into',
          explanation: 'Use "into" to show movement inside a container.'
        },
        {
          question: 'I climb _____ the tree.',
          answer: 'up',
          explanation: 'Use "up" to show upward movement.'
        }
      ]
    },
    {
      id: 'p2-topic-6',
      title: 'Gerund',
      description: 'Learning to use gerunds (verb + ing) as nouns in sentences',
      purpose: 'To teach students how to use gerunds (verb forms ending in -ing) as nouns in sentences.',
      questions: [
        {
          question: 'I like _____ (read) books.',
          answer: 'reading',
          explanation: 'Use the gerund form (verb + ing) when the verb acts as a noun.'
        },
        {
          question: '_____ (swim) is fun.',
          answer: 'Swimming',
          explanation: 'Use the gerund form as the subject of the sentence.'
        },
        {
          question: 'She enjoys _____ (dance).',
          answer: 'dancing',
          explanation: 'Use the gerund form after "enjoy".'
        },
        {
          question: '_____ (cook) is my hobby.',
          answer: 'Cooking',
          explanation: 'Use the gerund form as the subject of the sentence.'
        },
        {
          question: 'I love _____ (play) football.',
          answer: 'playing',
          explanation: 'Use the gerund form after "love".'
        },
        {
          question: '_____ (sing) makes me happy.',
          answer: 'Singing',
          explanation: 'Use the gerund form as the subject of the sentence.'
        },
        {
          question: 'He likes _____ (draw) pictures.',
          answer: 'drawing',
          explanation: 'Use the gerund form after "likes".'
        },
        {
          question: '_____ (write) is difficult.',
          answer: 'Writing',
          explanation: 'Use the gerund form as the subject of the sentence.'
        },
        {
          question: 'We enjoy _____ (watch) movies.',
          answer: 'watching',
          explanation: 'Use the gerund form after "enjoy".'
        },
        {
          question: '_____ (run) is good exercise.',
          answer: 'Running',
          explanation: 'Use the gerund form as the subject of the sentence.'
        }
      ]
    },
    {
      id: 'p2-topic-7',
      title: 'Is/Are there...?',
      description: 'Learning to ask questions about existence using "Is there" and "Are there"',
      purpose: 'To teach students how to form questions to ask about the existence of people, places, or things.',
      questions: [
        {
          question: '_____ a cat in the garden?',
          answer: 'Is there',
          explanation: 'Use "Is there" to ask about the existence of a singular thing.'
        },
        {
          question: '_____ any books on the table?',
          answer: 'Are there',
          explanation: 'Use "Are there" to ask about the existence of plural things.'
        },
        {
          question: '_____ a school near here?',
          answer: 'Is there',
          explanation: 'Use "Is there" to ask about the existence of a singular thing.'
        },
        {
          question: '_____ many students in the class?',
          answer: 'Are there',
          explanation: 'Use "Are there" to ask about the existence of plural things.'
        },
        {
          question: '_____ a park in this area?',
          answer: 'Is there',
          explanation: 'Use "Is there" to ask about the existence of a singular thing.'
        },
        {
          question: '_____ any shops nearby?',
          answer: 'Are there',
          explanation: 'Use "Are there" to ask about the existence of plural things.'
        },
        {
          question: '_____ a library in the town?',
          answer: 'Is there',
          explanation: 'Use "Is there" to ask about the existence of a singular thing.'
        },
        {
          question: '_____ many cars on the road?',
          answer: 'Are there',
          explanation: 'Use "Are there" to ask about the existence of plural things.'
        },
        {
          question: '_____ a hospital in the city?',
          answer: 'Is there',
          explanation: 'Use "Is there" to ask about the existence of a singular thing.'
        },
        {
          question: '_____ any trees in the park?',
          answer: 'Are there',
          explanation: 'Use "Are there" to ask about the existence of plural things.'
        }
      ]
    },
    {
      id: 'p2-topic-8',
      title: 'Modal Verbs',
      description: 'Learning to use modal verbs (can, could, may, might, must, should) to express different meanings',
      purpose: 'To teach students how to use modal verbs to express ability, permission, possibility, obligation, and advice.',
      questions: [
        {
          question: 'I _____ swim very well.',
          answer: 'can',
          explanation: 'Use "can" to express ability.'
        },
        {
          question: '_____ I borrow your pen?',
          answer: 'May',
          explanation: 'Use "may" to ask for permission politely.'
        },
        {
          question: 'You _____ study hard for the exam.',
          answer: 'should',
          explanation: 'Use "should" to give advice or make recommendations.'
        },
        {
          question: 'She _____ speak three languages.',
          answer: 'can',
          explanation: 'Use "can" to express ability.'
        },
        {
          question: 'You _____ wear a helmet when cycling.',
          answer: 'must',
          explanation: 'Use "must" to express strong obligation or necessity.'
        },
        {
          question: 'It _____ rain tomorrow.',
          answer: 'might',
          explanation: 'Use "might" to express possibility.'
        },
        {
          question: '_____ I go to the bathroom?',
          answer: 'May',
          explanation: 'Use "may" to ask for permission politely.'
        },
        {
          question: 'You _____ eat more vegetables.',
          answer: 'should',
          explanation: 'Use "should" to give advice about healthy eating.'
        },
        {
          question: 'He _____ play the piano beautifully.',
          answer: 'can',
          explanation: 'Use "can" to express ability.'
        },
        {
          question: 'You _____ be quiet in the library.',
          answer: 'must',
          explanation: 'Use "must" to express strong obligation.'
        }
      ]
    },
    {
      id: 'p2-topic-9',
      title: 'To-infinitive',
      description: 'Learning to use to-infinitive (to + base form of verb) in sentences',
      purpose: 'To teach students how to use to-infinitives to express purpose, intention, or as part of verb patterns.',
      questions: [
        {
          question: 'I want _____ (go) to the park.',
          answer: 'to go',
          explanation: 'Use "to + base form" after "want".'
        },
        {
          question: 'She likes _____ (read) books.',
          answer: 'to read',
          explanation: 'Use "to + base form" after "likes".'
        },
        {
          question: 'I need _____ (buy) some food.',
          answer: 'to buy',
          explanation: 'Use "to + base form" after "need".'
        },
        {
          question: 'He wants _____ (play) football.',
          answer: 'to play',
          explanation: 'Use "to + base form" after "wants".'
        },
        {
          question: 'We like _____ (watch) movies.',
          answer: 'to watch',
          explanation: 'Use "to + base form" after "like".'
        },
        {
          question: 'I want _____ (learn) English.',
          answer: 'to learn',
          explanation: 'Use "to + base form" after "want".'
        },
        {
          question: 'She needs _____ (study) hard.',
          answer: 'to study',
          explanation: 'Use "to + base form" after "needs".'
        },
        {
          question: 'He likes _____ (sing) songs.',
          answer: 'to sing',
          explanation: 'Use "to + base form" after "likes".'
        },
        {
          question: 'I want _____ (visit) my grandmother.',
          answer: 'to visit',
          explanation: 'Use "to + base form" after "want".'
        },
        {
          question: 'We need _____ (clean) the house.',
          answer: 'to clean',
          explanation: 'Use "to + base form" after "need".'
        }
      ]
    },
    {
      id: 'p2-topic-10',
      title: 'Modal Verb',
      description: 'Learning more advanced uses of modal verbs in different contexts',
      purpose: 'To teach students more complex uses of modal verbs in various situations and contexts.',
      questions: [
        {
          question: 'You _____ not smoke in public places.',
          answer: 'should',
          explanation: 'Use "should not" to give advice about what not to do.'
        },
        {
          question: '_____ I help you with your homework?',
          answer: 'Can',
          explanation: 'Use "can" to offer help or assistance.'
        },
        {
          question: 'You _____ wear warm clothes in winter.',
          answer: 'should',
          explanation: 'Use "should" to give advice about what to do.'
        },
        {
          question: '_____ I use your computer?',
          answer: 'May',
          explanation: 'Use "may" to ask for permission politely.'
        },
        {
          question: 'You _____ not talk during the test.',
          answer: 'must',
          explanation: 'Use "must not" to express prohibition.'
        },
        {
          question: '_____ I have some water?',
          answer: 'May',
          explanation: 'Use "may" to ask for permission politely.'
        },
        {
          question: 'You _____ eat breakfast every day.',
          answer: 'should',
          explanation: 'Use "should" to give advice about healthy habits.'
        },
        {
          question: '_____ I borrow your book?',
          answer: 'Can',
          explanation: 'Use "can" to ask for permission.'
        },
        {
          question: 'You _____ not run in the library.',
          answer: 'must',
          explanation: 'Use "must not" to express prohibition.'
        },
        {
          question: '_____ I open the window?',
          answer: 'May',
          explanation: 'Use "may" to ask for permission politely.'
        }
      ]
    }
  ]
}; 